import React, { useState } from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Star, Home, Users, DollarSign } from 'lucide-react';
import ChatModal from './ChatModal';

export default function CommunityCard({
  community = {},
  showActions = true
}) {
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const {
    id,
    name,
    address,
    image_url,
    rating,
    review_count,
    price_range,
    care_type,
    units = []
  } = community;

  return (
    <Card className="overflow-hidden transition-transform hover:-translate-y-1 shadow-soft rounded-soft py-0 border border-gray-200">
      {/* Image with overlaid title */}
      <div className="relative">
        <div className="w-full h-[240px] overflow-hidden">
          <img
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            src={image_url || "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"}
            alt={name}
          />
        </div>

        {/* Title overlay with gradient background */}
        <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/90 to-transparent">
          <h2 className="text-xl font-bold text-white mb-1">{name || "Community Name"}</h2>
          <div className="flex items-center text-white/90">
            <MapPin size={16} className="mr-1 flex-shrink-0" />
            <p className="text-base">{address || "Anytown, USA"}</p>
          </div>
        </div>

        {/* Current Resident Feedback - White card overlay */}
        <div className="absolute -bottom-16 left-4 right-4">
          <div className="bg-white p-5 rounded-soft shadow-soft border border-gray-100">
            <h3 className="text-base font-medium text-primary mb-3">Care Type</h3>
            <div className="flex justify-between">
              <div className="flex items-center space-x-2">
                <div className="bg-secondary/20 text-secondary text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center">
                  {care_type === 'assisted_living' ? 'AL' : care_type === 'memory_care' ? 'MC' : 'B'}
                </div>
                <div>
                  <div className="text-base font-medium text-foreground">
                    {care_type === 'assisted_living' ? 'Assisted Living' :
                     care_type === 'memory_care' ? 'Memory Care' : 'Both'}
                  </div>
                </div>
              </div>

              {rating && (
                <div className="flex items-center space-x-2">
                  <div className="bg-accent/20 text-accent text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center">
                    <Star size={14} />
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Rating</div>
                    <div className="font-medium text-foreground">{rating}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card content with spacing for the overlay */}
      <CardContent className="px-6 pt-24 pb-6 space-y-6">
        {/* Available Units */}
        <div>
          <div className="flex items-center mb-3">
            <Home size={18} className="mr-2 text-secondary" />
            <h3 className="text-lg font-medium text-primary">Available Units</h3>
          </div>
          {units && units.length > 0 && (
            <div className="mt-2 space-y-3">
              {units.filter(unit => unit.available).map((unit, index) => (
                <div key={index} className="flex justify-between items-center py-1 border-b border-gray-100">
                  <p className="text-base text-foreground">
                    {unit.room_type === 'studio' ? 'Studio' :
                     unit.room_type === 'one_bedroom' ? '1 Bedroom' :
                     unit.room_type === 'two_bedroom' ? '2 Bedroom' :
                     unit.room_type === 'shared' ? 'Shared Room' :
                     unit.room_type === 'suite' ? 'Suite' : unit.room_type}
                    {unit.bathrooms && ` - ${unit.bathrooms} Bath`}
                  </p>
                  <p className="text-base font-medium text-primary">${unit.price?.toLocaleString()}</p>
                </div>
              ))}
              {units.filter(unit => unit.available).length === 0 && (
                <p className="text-base text-muted-foreground italic">No units currently available</p>
              )}
            </div>
          )}
        </div>

        {/* Price Range */}
        {price_range && (
          <div>
            <div className="flex items-center mb-1">
              <DollarSign size={16} className="mr-1 text-accent" />
              <span className="text-sm text-muted-foreground">Price Range</span>
            </div>
            <p className="text-xl font-bold text-primary">{price_range}</p>
          </div>
        )}

        {/* Care Type */}
        <div className="flex flex-wrap gap-2 mt-2">
          <span
            className="inline-flex items-center rounded-full px-3 py-1 text-sm font-medium border-transparent bg-secondary text-secondary-foreground"
          >
            <Users size={14} className="mr-1" />
            {care_type === 'assisted_living' ? 'Assisted Living' :
             care_type === 'memory_care' ? 'Memory Care' : 'Both'}
          </span>
        </div>
      </CardContent>

      {/* Action buttons */}
      {showActions && (
        <CardFooter className="px-6 pb-6 pt-2 grid grid-cols-2 gap-3">
          <Button className="w-full py-5 text-base font-medium bg-primary hover:bg-primary-hover">View Details</Button>
          <Button
            variant="outline"
            className="w-full py-5 text-base font-medium border-gray-200 hover:bg-accent hover:text-accent-foreground"
            onClick={(e) => {
              e.preventDefault(); // Prevent link navigation
              e.stopPropagation(); // Stop event from bubbling up to parent Link
              setChatModalOpen(true);
            }}
          >
            Contact
          </Button>
        </CardFooter>
      )}

      {/* Chat Modal */}
      <ChatModal
        isOpen={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        facility={community}
      />
    </Card>
  );
}
